import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchAllOffers } from "./paging";
import { apiDelete, apiGet, apiPost, apiPut } from "./client";
import { SCROLLS_OFF, recipeQuery, recipesQuery } from "./recipeQuery";
import type {
  HistoryPoint, InventoryAnalysis, InventoryOut, ItemCrafting, ItemMatch,
  ItemObservationPage, MarketItemPage,
  MarketLotPage, Mode, NpcPrice, OfferMode, OfferPage, PackageLot, PricedMode,
  RecipeCatalogue, RecipeDetail, ScanDetail, ScanSummary,
  TraderDetail, TraderMatch, TraderScanChanges, TradesPage,
  WatchEntry, WatchlistPage,
  TelegramCode, TelegramState, TelegramWebhookInfo,
} from "./types";

export type Sort = "item" | "spread" | "buy" | "sell";

export function useScans(filters: { source?: string; city?: string } = {}) {
  const params = new URLSearchParams();
  if (filters.source) params.set("source", filters.source);
  if (filters.city) params.set("city", filters.city);
  const suffix = params.toString() ? `?${params}` : "";
  return useQuery({
    queryKey: ["scans", filters],
    queryFn: () => apiGet<ScanSummary[]>(`/scans${suffix}`),
  });
}

export function useScanDetail(scanUid: string, sort: Sort) {
  return useQuery({
    queryKey: ["scan", scanUid, sort],
    queryFn: () => apiGet<ScanDetail>(`/scans/${scanUid}?sort=${sort}`),
    // The items view at "/" reads the newest scan's uid out of the scan list,
    // so for one render it has no uid at all. Without this guard that render
    // fires GET /scans/?sort=item — a 404 on every cold visit to the home page.
    enabled: scanUid !== "",
  });
}

export function useScanOffers(scanUid: string, item: string | null) {
  return useQuery({
    queryKey: ["scan-offers", scanUid, item],
    queryFn: () =>
      apiGet<OfferPage>(
        // The limit is spelled out rather than inherited from the server's
        // default, because the panel's label states it and the two must not
        // drift apart. next_cursor is not consumed anywhere.
        `/scans/${scanUid}/offers?limit=200${item ? `&item=${encodeURIComponent(item)}` : ""}`,
      ),
    enabled: item !== null,
  });
}

/** A whole mode of one scan, for the mode pages. Separate from useScanOffers,
 *  which fetches one item's offers for the panel under the item table: that one
 *  wants 200 rows of one item, this one wants every row of one mode. */
export function useScanOffersByMode(scanUid: string, mode: Mode) {
  return useQuery({
    queryKey: ["scan-offers-mode", scanUid, mode],
    queryFn: () => fetchAllOffers((path) => apiGet<OfferPage>(path), scanUid, mode),
    // The same guard useScanDetail and useScanPackages carry: at "/" the uid
    // arrives from the scan list a render late, and GET /scans//offers is a 404
    // on every cold visit to the home page.
    enabled: scanUid !== "",
  });
}

/** The header's ItemSearch is the only consumer, and it passes a value that has
 *  already been trimmed and debounced.
 *
 *  Disabled on an empty query rather than enabled-and-ignored: the server
 *  answers q="" with [] without touching the database, so firing anyway would
 *  spend a round trip on an answer both ends already know. placeholderData holds
 *  the previous list while the next one is in flight — without it the dropdown
 *  empties and re-fills on every keystroke, which is the flicker this option
 *  exists for. */
export function useItemSearch(q: string) {
  return useQuery({
    queryKey: ["item-search", q],
    queryFn: () => apiGet<ItemMatch[]>(`/items?q=${encodeURIComponent(q)}&limit=20`),
    enabled: q !== "",
    placeholderData: (prev) => prev,
  });
}

export function useScanPackages(scanUid: string) {
  return useQuery({
    queryKey: ["scan-packages", scanUid],
    queryFn: () => apiGet<PackageLot[]>(`/scans/${scanUid}/packages`),
    // The same guard useScanDetail needs, for the same reason: at "/" the uid
    // arrives from the scan list a render late, and GET /scans//packages is a
    // 404 on every cold visit to the home page.
    enabled: scanUid !== "",
  });
}

export function useItemHistory(itemId: number, mode: PricedMode | null) {
  return useQuery({
    queryKey: ["item-history", itemId, mode],
    queryFn: () =>
      apiGet<HistoryPoint[]>(
        `/items/${itemId}/history?bucket=day${mode ? `&mode=${mode}` : ""}`,
      ),
  });
}

export function useItemOffers(itemId: number) {
  return useQuery({
    queryKey: ["item-offers", itemId],
    queryFn: () => apiGet<ItemObservationPage>(`/items/${itemId}/offers?limit=1000`),
  });
}

/** The trader page's single request. `nick` goes through encodeURIComponent
 *  for the same reason traderHref does — it is a player's own name, not a slug.
 *  Disabled on an empty nick: the route cannot produce one, but the guard keeps
 *  this hook's contract the same as every other here. */
export function useTraderDetail(nick: string) {
  return useQuery({
    queryKey: ["trader-detail", nick],
    queryFn: () => apiGet<TraderDetail>(`/traders/${encodeURIComponent(nick)}/detail`),
    enabled: nick !== "",
  });
}

/** One appearance's changes, fetched as its history row is expanded.
 *
 *  `enabled` on a null uid rather than a separate call site: the row is closed
 *  far more often than it is open, and the summary line above it already says
 *  how much there is to see. */
export function useTraderScanChanges(nick: string, scanUid: string | null) {
  return useQuery({
    queryKey: ["trader-scan-changes", nick, scanUid],
    queryFn: () => apiGet<TraderScanChanges>(
      `/traders/${encodeURIComponent(nick)}/scans/${encodeURIComponent(scanUid ?? "")}/changes`,
    ),
    enabled: nick !== "" && scanUid !== null,
  });
}

export type AdminKind = "items" | "traders";

export interface MergeImpact {
  name: string;
  offers: number;
  scans: number;
  aliases: number;
  collapses: number | null;
  /** True when a vendor price would die with this item. Whether, not how much:
   *  what the confirmation has to say is that a number somebody typed is about
   *  to go. Always false for a trader — only items are priced. */
  npc_price: boolean;
}

/** What a merge or delete would cost, read from the server. Deliberately not
 *  estimated in the client: this figure is the only thing between an owner and
 *  an irreversible act, and a number the page guessed is not good enough.
 *
 *  `enabled` is the caller's confirmation-is-open flag, not just `id > 0`:
 *  every owner's item and trader page mounts this hook, so without that extra
 *  gate it fires a round trip on every page load — nobody asked, and it warms
 *  the cache so a delete dialog can paint a page-load-era count for one
 *  render before the fresh number arrives. */
export function useImpact(
  kind: AdminKind, id: number, into: number | null, enabled: boolean,
) {
  return useQuery({
    queryKey: ["admin-impact", kind, id, into],
    queryFn: () => apiGet<MergeImpact>(
      `/admin/${kind}/${id}/impact${into === null ? "" : `?into=${into}`}`),
    enabled: enabled && id > 0,
  });
}

export function useTraderSearch(q: string) {
  return useQuery({
    queryKey: ["trader-search", q],
    queryFn: () => apiGet<TraderMatch[]>(
      `/traders?q=${encodeURIComponent(q)}&limit=20`),
    enabled: q !== "",
    placeholderData: (prev) => prev,
  });
}

/** After a merge or a delete the whole cache is stale: scan counts moved, the
 *  subject is gone, and its item or trader pages no longer resolve. Clearing
 *  everything is right here — this happens at most a few times a week and the
 *  alternative is enumerating every key a merge can touch. */
export function useMergeSubject(kind: AdminKind) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({ id, into }: { id: number; into: number }) =>
      apiPost<{ ok: boolean; into: string }>(`/admin/${kind}/${id}/merge`, { into_id: into }),
    onSuccess: () => cache.invalidateQueries(),
  });
}

export function useDeleteSubject(kind: AdminKind) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete<{ ok: boolean }>(`/admin/${kind}/${id}`),
    onSuccess: () => cache.invalidateQueries(),
  });
}

/** Every item ever scanned, with its latest known price per mode.
 *
 *  No `enabled` guard, unlike every other hook here: this one needs no uid,
 *  which is the whole point of it. All four all-time item pages read this one
 *  response, so react-query serves index → buy → sell → craft from a single
 *  request rather than four. */
export function useMarketItems() {
  return useQuery({
    queryKey: ["market-items"],
    queryFn: () => apiGet<MarketItemPage>("/market/items"),
  });
}

/** Every distinct lot ever scanned. Separate from useMarketItems because a lot
 *  is not an item row: it has one price between several items, and no per-item
 *  price to put in any of that table's columns. */
export function useMarketLots() {
  return useQuery({
    queryKey: ["market-lots"],
    queryFn: () => apiGet<MarketLotPage>("/market/packages"),
  });
}

/** The whole catalogue, costed. One request per reading: the server sends all
 *  995 rows because sorting a table you are already reading should not be a
 *  round trip.
 *
 *  `countScrolls` is the one control that is a second request rather than a
 *  render — it changes which ingredients the totals are summed from — so it
 *  rides in the key and the two readings cache side by side. Switching back is
 *  then instant. See api/recipeQuery.ts. */
export function useRecipes(countScrolls: boolean) {
  const { queryKey, path } = recipesQuery(countScrolls);
  return useQuery({
    queryKey,
    queryFn: () => apiGet<RecipeCatalogue>(path),
  });
}

export function useRecipe(recipeId: number, countScrolls: boolean) {
  const { queryKey, path } = recipeQuery(recipeId, countScrolls);
  return useQuery({
    queryKey,
    queryFn: () => apiGet<RecipeDetail>(path),
    // A hand-typed /recipes/abc gives NaN; without this the page fires
    // GET /recipes/NaN and renders an error it could have avoided.
    enabled: Number.isInteger(recipeId) && recipeId > 0,
  });
}

/** What makes this item and what it is used to make. Its own hook rather than
 *  part of the item page's other queries: it is a panel that is absent for most
 *  items, and a failure of it must not take the price history down with it. */
export function useItemCrafting(itemId: number) {
  return useQuery({
    queryKey: ["item-crafting", itemId],
    queryFn: () => apiGet<ItemCrafting>(`/items/${itemId}/crafting`),
    enabled: Number.isInteger(itemId) && itemId > 0,
  });
}

/** Recording a vendor price changes two things a reader is looking at: this
 *  item's own panel, and every recipe cost the item appears in. Both are
 *  invalidated, because a price that updates one and not the other is worse
 *  than one that updates neither. */
export function useSetNpcPrice(itemId: number) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (price: number) =>
      apiPut<NpcPrice>(`/items/${itemId}/npc-price`, { price }),
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: ["item-crafting", itemId] });
      cache.invalidateQueries({ queryKey: ["recipes"] });
      cache.invalidateQueries({ queryKey: ["recipe"] });
    },
  });
}

/** A vendor price for a name that may not be an item yet. The id-keyed twin
 *  above cannot reach these: there is no id until this call mints one, which
 *  is also why the invalidations are not narrowed to one item — the caller has
 *  no id to narrow them with, and the recipe pages are what it changes. */
export function useSetNpcPriceByName() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({ name, price }: { name: string; price: number }) =>
      apiPut<NpcPrice>("/npc-prices/by-name", { name, price }),
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: ["item-crafting"] });
      cache.invalidateQueries({ queryKey: ["recipes"] });
      cache.invalidateQueries({ queryKey: ["recipe"] });
    },
  });
}

export function useClearNpcPrice(itemId: number) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete<NpcPrice>(`/items/${itemId}/npc-price`),
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: ["item-crafting", itemId] });
      cache.invalidateQueries({ queryKey: ["recipes"] });
      cache.invalidateQueries({ queryKey: ["recipe"] });
    },
  });
}

/** The deal hunter's own history. Behind a per-user feature, so a 403 here
 *  is an ordinary answer for an account that has not been given it. */
export function useTrades(liveOnly = false) {
  const suffix = liveOnly ? "?live_only=true" : "";
  return useQuery({
    queryKey: ["trades", liveOnly],
    queryFn: () => apiGet<TradesPage>(`/trades${suffix}`),
    retry: false,
  });
}

/** The signed-in account's watchlist.
 *
 *  The one query in this app that may poll, and only when its caller asks it
 *  to: `/watch` is the page whose purpose is to be left open while a scan is
 *  walking a town, and a hit nobody is told about is the feature not working.
 *  Every other page keeps the fetch-on-mount contract. */
export function useWatchlist({ poll = false }: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: ["watchlist"],
    queryFn: () => apiGet<WatchlistPage>("/watchlist"),
    // The interval is the caller's, not the hook's: `/watch` is the page
    // someone leaves open while a scan walks a town, and a star on an item
    // page has nothing to learn from polling — its state only changes by a
    // click that already invalidates this key. Baked into the hook, the star
    // dragged the poll onto `/items/:id` and `/` with it. Same query key
    // either way, so every star still shares one cached request.
    refetchInterval: poll ? 60_000 : false,
  });
}

/** Star a series, or change what it is waiting for. One endpoint for both:
 *  omitting `target_price` leaves an existing one alone, so starring never
 *  wipes a target the user typed earlier. */
export function useSetWatch() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, mode, target }: {
      itemId: number; mode: OfferMode; target?: number | null;
    }) =>
      apiPut<WatchEntry>(
        `/watchlist/${itemId}/${mode}`,
        target === undefined ? {} : { target_price: target },
      ),
    onSuccess: () => cache.invalidateQueries({ queryKey: ["watchlist"] }),
  });
}

export function useUnwatch() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, mode }: { itemId: number; mode: OfferMode }) =>
      apiDelete<{ ok: boolean }>(`/watchlist/${itemId}/${mode}`),
    onSuccess: () => cache.invalidateQueries({ queryKey: ["watchlist"] }),
  });
}

/** What this user is carrying, per character and pooled.
 *
 *  Cheap and always loaded: it is what the page opens with, and it is the
 *  thing that says whether there is anything to analyse at all. */
export function useInventory() {
  return useQuery({
    queryKey: ["inventory"],
    queryFn: () => apiGet<InventoryOut>("/inventory"),
  });
}

/** The whole catalogue costed against the bag.
 *
 *  `enabled` is the button. This is 995 rows of arithmetic over a price map of
 *  every name the catalogue mentions, and it is not what a reader opening the
 *  page to check what they are carrying asked for — so it runs when they say
 *  so, and then stays in the cache under its own key.
 *
 *  `countScrolls` rides in the key for the same reason it does on the recipes
 *  page: it reshapes the ingredient set, so it is a refetch and the two
 *  readings cache side by side. */
export function useInventoryAnalysis(countScrolls: boolean, enabled: boolean) {
  const suffix = countScrolls ? "" : `?scrolls=${SCROLLS_OFF}`;
  return useQuery({
    queryKey: ["inventory-analysis", countScrolls],
    queryFn: () => apiGet<InventoryAnalysis>(`/inventory/analysis${suffix}`),
    enabled,
  });
}

/** Whether Telegram notifications can work on this deployment, and this
 *  account's chat.
 *
 *  `poll` is the caller's, the way `useWatchlist`'s is: the settings page turns
 *  it on only while an unspent link code is on screen. The link itself happens
 *  in another application — the reader sends the code to the bot and comes back
 *  — so without a poll the page cannot ever say "that worked" and the only way
 *  to find out is a reload. Ten seconds, not the watchlist's sixty: this window
 *  is at most ten minutes long and ends the moment it succeeds. */
export function useTelegramState({ poll = false }: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: ["telegram"],
    queryFn: () => apiGet<TelegramState>("/settings/telegram"),
    refetchInterval: poll ? 10_000 : false,
  });
}

/** A fresh code to send to the bot, replacing any this account had.
 *
 *  Invalidates ["telegram"] because minting is not read-only on the server: it
 *  deletes the account's previous code. Nothing in the state response says so
 *  today, and this is still the right call — a mutation that leaves a stale
 *  read of the thing it mutated is a bug waiting for the next field. */
export function useMintCode() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<TelegramCode>("/settings/telegram/code"),
    onSuccess: () => cache.invalidateQueries({ queryKey: ["telegram"] }),
  });
}

export function useUnlinkTelegram() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete<{ ok: boolean }>("/settings/telegram"),
    onSuccess: () => cache.invalidateQueries({ queryKey: ["telegram"] }),
  });
}

/** Telegram's own account of how delivery is going. Owners only — a 403 is
 *  what everybody else gets — so the settings page mounts its panel, and with
 *  it this hook, only for an owner. There is no `enabled` flag for the same
 *  reason there is no 403 branch: the request is never issued at all.
 *
 *  Its own key rather than ["telegram", …]: an unlink invalidates that prefix,
 *  and this query is a round trip to Telegram's API rather than to ours. */
export function useTelegramWebhook() {
  return useQuery({
    queryKey: ["telegram-webhook"],
    queryFn: () => apiGet<TelegramWebhookInfo>("/settings/telegram/webhook-info"),
  });
}
